import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { useAllRiskDetails } from "../hooks/useAllRiskDetails";
import { DrawerFailed, DrawerLoading } from "./DrawerShell";
import { DeviceDetailDrawer } from "./DeviceInventoryPanel";
import { IdentityDetailDrawer } from "./IdentityInventoryPanel";
import RiskDetailTabs from "./RiskDetailTabs";

const PATH = { identity: "identities", device: "devices" };

// A device's or an identity's details on the Security Operations page (/soc), opened from the "Most
// exposed" cards under them, in place - never a jump to Security Insights. The content is Security
// Insights' own detail view (DeviceDetailDrawer / IdentityDetailDrawer, laid out inline), read from the
// same routes. Inside it, a finding opens its All Risks tabs here, and a logged-on device or identity
// replaces this one (`onSwitch`).
// `target` is { kind: "identity" | "device", id }. `riskDetailsKey` (the queue's changeKey) re-reads
// the full risks after a write or Refresh.
const SocExposedDetails = ({ target, organizationId, onClose, onSwitch, riskDetailsKey }) => {
  const apiCall = useApi();
  const { request, riskFor, version } = useAllRiskDetails({ resetKey: riskDetailsKey });
  // What was read, tagged with the target it belongs to: until the tag matches the current target,
  // the panel is loading, so a device view is never handed the identity it is replacing.
  const key = `${target.kind}:${target.id}`;
  const [loaded, setLoaded] = useState({ key: null, detail: undefined });
  // undefined: loading; null: failed; else the detail.
  const detail = loaded.key === key ? loaded.detail : undefined;
  const [attempt, setAttempt] = useState(0);
  const [openRiskId, setOpenRiskId] = useState(null);

  useEffect(() => {
    let live = true;
    setLoaded({ key: null, detail: undefined });
    setOpenRiskId(null);
    apiCall("GET", `/defender-identity/${PATH[target.kind]}/${encodeURIComponent(target.id)}`)
      .then((data) => { if (live) setLoaded({ key, detail: data || null }); })
      .catch(() => { if (live) setLoaded({ key, detail: null }); });
    return () => { live = false; };
  }, [apiCall, key, target.kind, target.id, attempt]);

  useEffect(() => {
    if (openRiskId) request();
  }, [openRiskId, request, version]);

  const noun = target.kind === "identity" ? "identity" : "device";
  if (detail === undefined) {
    return (
      <div className="rounded-xl border border-border bg-theme-card-solid p-5" data-testid="soc-exposed-details">
        <DrawerLoading testId="soc-exposed-loading" message={`Loading ${noun} details…`} />
      </div>
    );
  }
  if (detail === null) {
    return (
      <div className="rounded-xl border border-border bg-theme-card-solid p-5" data-testid="soc-exposed-details">
        <DrawerFailed testId="soc-exposed-failed" message={`The ${noun} details could not be loaded.`}
          onRetry={() => setAttempt((a) => a + 1)} />
        <button type="button" onClick={onClose} className="mt-2 text-xs text-content-muted hover:underline">Close</button>
      </div>
    );
  }

  const risk = openRiskId ? riskFor(openRiskId) : undefined;
  const handlers = {
    inline: true,
    // Top 5 findings, the rest behind "+ N more" - a device can carry dozens of recommendations.
    findingsLimit: 5,
    onClose,
    onOpenRisk: (id) => setOpenRiskId((cur) => (cur === id ? null : id)),
  };

  return (
    <div className="space-y-3" data-testid="soc-exposed-details">
      {openRiskId && (
        <section aria-label="Finding details" data-testid="soc-exposed-finding"
          className="rounded-xl border border-border bg-theme-card-solid p-4 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-content-primary">Finding details</h3>
            <button type="button" onClick={() => setOpenRiskId(null)} aria-label="Close finding"
              className="text-content-muted hover:text-content-primary">✕</button>
          </div>
          {risk === null && (
            <p className="text-xs text-content-muted" data-testid="soc-exposed-finding-partial">The full risk details couldn't be loaded.</p>
          )}
          <RiskDetailTabs risk={risk || { id: openRiskId, riskDescription: null }} loading={risk === undefined} />
        </section>
      )}
      {target.kind === "identity" ? (
        <IdentityDetailDrawer {...handlers} detail={detail} organizationId={organizationId}
          onOpenDevice={(id) => onSwitch({ kind: "device", id })} />
      ) : (
        <DeviceDetailDrawer {...handlers} detail={detail}
          onOpenIdentity={(id) => onSwitch({ kind: "identity", id })} />
      )}
    </div>
  );
};

export default SocExposedDetails;
